import {
	PaymentMethodType,
	usePaymentSection_UpdateInvoiceFromPaymentSessionMutation,
	usePaymentSection_CreateIntentForInvoicePaymentMutation,
	InstallmentStatus,
	UsePaymentSectionInvoiceFragment,
	InstallmentAmountInput,
} from '#types';
import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useToast } from '../../../../hooks/useToast';
import { gql } from '@apollo/client';
import rpcShared from '@rockpapercoin/rpc-shared';
import validations from '../../../../lib/helpers/validations';
import { SELECTION_OPTIONS } from './InvoiceViewGratuitySelection';
import Globals from '../../../../lib/Globals';
import { useBlockingRouter } from '../../../../hooks/useBlockingRouter';
import { client } from '../../../../context/ApolloContextProvider/client';
import {
	createPaymentAttemptFingerprint,
	getOrCreatePaymentAttempt,
	PaymentAttempt,
} from '../../../../lib/helpers/paymentAttempt';

const PaymentMethod = rpcShared.invoices.PaymentMethod;

const usePaymentSectionPaymentInstallmentFragment = gql`
	fragment usePaymentSectionPaymentInstallment on PaymentInstallment {
		id
		amountDue
		amountPaid
		gratuityPaid
		paymentDate
		status
		scheduledPaymentDate
	}
`;

export const usePaymentSectionInvoiceFragment = gql`
	fragment usePaymentSectionInvoice on Invoice {
		id
		status
		isPastDue
		allowsGratuity
		discountAmount
		gratuityAmount
		contact {
			id
			firstName
			lastName
			customer {
				id
				email
			}
		}
		organization {
			id
			name
			country
			cardConvenienceFeePercentage
			achConvenienceFeePercentage
		}
		lineItems {
			id
			name
			taxPercentage
			rate
			quantity
		}
		paymentInstallments {
			...usePaymentSectionPaymentInstallment
		}
	}
	${ usePaymentSectionPaymentInstallmentFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentSectionCreateSecretsForInvoicePayment = gql`
	mutation paymentSection_createSecretsForInvoicePayment(
		$data: CreateSecretsForInvoicePaymentInput!
	) {
		createSecretsForInvoicePayment(data: $data) {
			customerSecret
			invoiceId
			currency
			onBehalfOf
			excludedPaymentMethodTypes
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentSectionCreateIntentForInvoicePayment = gql`
	mutation paymentSection_createIntentForInvoicePayment(
		$data: CreateIntentForInvoicePaymentInput!
	) {
		createIntentForInvoicePayment(data: $data) {
			id
			clientSecret
			amount
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentSectionGetPaymentSourcesForCustomersQuery = gql`
	query paymentSection_getPaymentSourcesForCustomer(
		$where: UserByIdUniqueInput!
	) {
		getPaymentSourcesForCustomer(where: $where) {
			data {
				id
				bank_name
				object
				last4
				brand
				metadata {
					key
					value
				}
			}
			default_source
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentSection_updateInvoiceFromPaymentSessionMutation = gql`
	mutation paymentSection_updateInvoiceFromPaymentSession(
		$data: UpdateInvoiceFromPaymentSessionInput!
	) {
		updateInvoiceFromPaymentSession(data: $data) {
			...usePaymentSectionInvoice
		}
	}
	${ usePaymentSectionInvoiceFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const paymentSection_updateInvoiceAutoPayMutation = gql`
	mutation paymentSection_updateInvoiceAutoPay(
		$where: InvoiceWhereUniqueInput!
		$data: InvoiceAutoPayUpdateCustomInput!
	) {
		updateInvoiceAutoPay(where: $where, data: $data) {
			id
			paymentInstallments {
				id
				status
				scheduledPaymentDate
			}
			...usePaymentSectionInvoice
		}
	}
	${ usePaymentSectionInvoiceFragment }
`;

type ValidationType = {
	gratuityValidation?: string;
	paymentMethod?: string;
	receiptEmails?: string;
	ccField?: string;
};

type StoredInvoicePaymentData = {
	sessionPaymentInstallmentId: string;
	paymentInstallmentIds: string[];
	gratuityAmount?: number;
	activateAutoPay: boolean;
	receiptEmails: string[];
	invoiceId: string;
};

export type InvoicePaymentSessionSecrets = {
	customerSessionClientSecret: string;
	currency: string;
	onBehalfOf?: string;
	excludedPaymentMethodTypes: string[];
};

type UsePaymentSectionProps = {
	invoice: UsePaymentSectionInvoiceFragment;
	initialSessionSecrets: InvoicePaymentSessionSecrets;
	guestToken?: string;
	selectedInstallments: {
		id: string;
		dueDate?: string | Date | null;
		amountDue?: number | null;
	}[];
	onSuccess: () => void;
	onClose: () => void;
};

/**
 * This hook handles getting the data in the case that the payer of paymentInstallments is authenticated
 */
export const usePaymentSection = ( {
	invoice,
	initialSessionSecrets,
	guestToken,
	selectedInstallments,
	onSuccess,
	onClose,
}: UsePaymentSectionProps ) => {
	const router = useBlockingRouter();
	const { showError } = useToast();
	const [ loading, setLoading ] = useState( false );
	const [ hasSubmitted, setHasSubmitted ] = useState( false );
	const [ gratuityAmount, setGratuityAmount ] = useState( 0 );
	const [ paymentMethodType, setPaymentMethodType ] = useState<
		PaymentMethodType | undefined
	>();
	const [ validation, setValidation ] = useState<ValidationType | undefined>();
	const [ receiptEmails, setReceiptEmails ] = useState<string | undefined>();
	const [ total, setTotal ] = useState( 0 );
	const [ gratuitySelection, setGratuitySelection ] = useState<string>(
		SELECTION_OPTIONS.NONE
	);
	const [ autoPaySelection, setAutoPaySelection ] = useState( false );
	const [ sessionSecrets, setSessionSecrets ] = useState<
		InvoicePaymentSessionSecrets | undefined
	>( initialSessionSecrets );
	const [ paymentValidationErrorModalOpen, setPaymentValidationErrorModalOpen ] =
		useState( false );
	const invoiceId = invoice?.id;
	const paymentInstallments = invoice?.paymentInstallments;
	const sessionPaymentInstallmentId = selectedInstallments[ 0 ]?.id;

	const token = guestToken;
	const cardConvenienceFeePercentage =
		invoice?.organization?.cardConvenienceFeePercentage || 0;
	const achConvenienceFeePercentage =
		invoice?.organization?.achConvenienceFeePercentage || 0;

	const [
		updateInvoiceFromPaymentSession,
		{
			loading: updateInvoiceFromPaymentSessionLoading,
			error: updateInvoiceFromPaymentSessionError,
		},
	] = usePaymentSection_UpdateInvoiceFromPaymentSessionMutation( {
		/* Refresh both the core invoice data and its separately queried history before
		showing the post-payment success state. */
		fetchPolicy: 'no-cache',
		refetchQueries: [ 'invoiceDetail_getInvoiceWhere', 'invoiceViewInvoice_getInvoiceHistory', ],
		awaitRefetchQueries: true,
	} );

	const transformedReceiptEmails = useMemo(
		() => receiptEmails?.split( ',' ).map( ( email ) => email.trim() ) || [],
		[ receiptEmails ]
	);

	useEffect( () => {
		setAutoPaySelection(
			paymentInstallments?.some(
				( installment ) => installment.scheduledPaymentDate
			) || false
		);
	}, [ paymentInstallments, setAutoPaySelection ] );

	// the only installments that can be auto-paid are those currently unpaid, which we further filter by those the user *intends* to pay
	const autoPayableInstallmentCount = useMemo(
		() =>
			paymentInstallments?.filter(
				( installment ) =>
					installment.status === InstallmentStatus.Unpaid &&
					selectedInstallments?.every(
						( selectedInstallment ) => selectedInstallment.id !== installment.id
					)
			).length || 0,
		[ paymentInstallments, selectedInstallments ]
	);

	const shouldRenderAutoPayOptions =
		!invoice?.isPastDue && autoPayableInstallmentCount;

	const validate = useCallback( () => {
		setHasSubmitted( ( prevState ) => ( !prevState ? true : prevState ) );
		let isValid = true;
		let emailValidation: string | undefined;
		let gratuityValidation: string | undefined;
		if ( receiptEmails ) {
			const receiptEmailsArr = receiptEmails.split( ',' );
			if ( receiptEmailsArr.length ) {
				for ( const email of receiptEmailsArr ) {
					emailValidation = validations.validateEmail( email.trim() );
					if ( emailValidation ) {
						isValid = false;
						break;
					}
				}
			}
		}
		if ( !gratuityAmount && gratuitySelection === SELECTION_OPTIONS.CUSTOM ) {
			isValid = false;
			gratuityValidation = 'Enter in a gratuity or select "none"';
		}
		if ( !paymentMethodType ) {
			isValid = false;
		}

		setValidation( ( prevState ) => ( {
			...prevState,
			paymentMethod: !paymentMethodType
				? 'Selecting a payment method is required'
				: undefined,
			receiptEmails: emailValidation ? emailValidation : undefined,
			gratuityValidation: gratuityValidation ? gratuityValidation : undefined,
		} ) );
		return isValid;
	}, [
		receiptEmails,
		gratuityAmount,
		gratuitySelection,
		paymentMethodType
	] );

	const installmentsForValidation = useMemo( () => {
		if (
			!invoice ||
			!selectedInstallments ||
			!Array.isArray( selectedInstallments ) ||
			!paymentMethodType ||
			!paymentInstallments
		) {
			return undefined;
		}

		const calculatedValues = rpcShared.invoices.calculateInvoicePayment( {
			invoice,
			paymentMethod: rpcShared.invoices.paymentMethodIsCard( paymentMethodType )
				? PaymentMethod.Card
				: paymentMethodType === PaymentMethodType.us_bank_account ||
				  paymentMethodType === PaymentMethodType.acss_debit ||
				  /* in the backend in
						`src/graphqlEndpoint/resolvers/Invoice/isPaymentMethodCard.ts` we
						count link as not a card, so we do the same here */
				  paymentMethodType === PaymentMethodType.link
					? PaymentMethod.Ach
					: null,
			selectedPaymentInstallmentIds: selectedInstallments.map(
				( installment ) => installment.id
			),
			gratuity: {
				value:
					gratuitySelection === SELECTION_OPTIONS.CUSTOM
						? gratuityAmount
						: parseInt( gratuitySelection ),
				isPercentage:
					gratuitySelection !== SELECTION_OPTIONS.CUSTOM &&
					gratuitySelection !== SELECTION_OPTIONS.NONE,
			},
		} );

		// Find the last installment to be paid (by payment date or due date)
		const sortedSelectedInstallments = [ ...selectedInstallments ].sort(
			( a, b ) => {
				const aInstallment = paymentInstallments.find( ( pi ) => pi.id === a.id );
				const bInstallment = paymentInstallments.find( ( pi ) => pi.id === b.id );

				const aDate =
					aInstallment?.paymentDate ||
					aInstallment?.scheduledPaymentDate ||
					a.dueDate;
				const bDate =
					bInstallment?.paymentDate ||
					bInstallment?.scheduledPaymentDate ||
					b.dueDate;

				if ( !aDate ) {
					return -1;
				}
				if ( !bDate ) {
					return 1;
				}

				return new Date( aDate ).getTime() - new Date( bDate ).getTime();
			}
		);

		const lastInstallmentId =
			sortedSelectedInstallments[ sortedSelectedInstallments.length - 1 ]?.id;

		// Build the installments array for validation
		return selectedInstallments
			.map( ( selectedInstallment ) => {
				const dbInstallment = paymentInstallments.find(
					( pi ) => pi.id === selectedInstallment.id
				);
				if ( !dbInstallment || !dbInstallment.amountDue ) {
					return null;
				}

				// Apply gratuity only to the last installment to be paid
				const installmentGratuity =
					selectedInstallment.id === lastInstallmentId
						? calculatedValues.gratuity
						: 0;

				// Calculate convenience fee for this installment
				const amountToBePaid = rpcShared.math.add(
					dbInstallment.amountDue,
					installmentGratuity
				);
				const convenienceFee = rpcShared.invoices.calculateConvenienceFee( {
					organization: {
						cardConvenienceFeePercentage,
						achConvenienceFeePercentage,
					},
					amountToBePaid,
					isPayingByCard:
						rpcShared.invoices.paymentMethodIsCard( paymentMethodType ),
				} );

				return {
					id: dbInstallment.id,
					amountDue: dbInstallment.amountDue,
					gratuity: installmentGratuity,
					convenienceFee,
				};
			} )
			.filter( ( item ): item is InstallmentAmountInput => item !== null );
	}, [
		invoice,
		selectedInstallments,
		paymentMethodType,
		gratuitySelection,
		gratuityAmount,
		paymentInstallments,
		cardConvenienceFeePercentage,
		achConvenienceFeePercentage,
	] );
	const paymentElementAmount = useMemo( () => {
		const sessionInstallment = installmentsForValidation?.find(
			( installment ) => installment.id === sessionPaymentInstallmentId
		);
		return sessionInstallment
			? rpcShared.math.add(
				rpcShared.math.add(
					sessionInstallment.amountDue,
					sessionInstallment.gratuity
				),
				sessionInstallment.convenienceFee
			  )
			: 0;
	}, [ installmentsForValidation, sessionPaymentInstallmentId ] );
	const paymentAttemptFingerprint = useMemo(
		() =>
			createPaymentAttemptFingerprint(
				sessionPaymentInstallmentId,
				total,
				paymentMethodType
			),
		[
			sessionPaymentInstallmentId,
			total,
			paymentMethodType
		]
	);
	const paymentAttemptRef = useRef<PaymentAttempt | null>( null );

	useEffect( () => {
		const calculateInvoice = () => {
			if (
				invoice &&
				selectedInstallments &&
				Array.isArray( selectedInstallments )
			) {
				const values = rpcShared.invoices.calculateInvoicePayment( {
					invoice,
					paymentMethod: rpcShared.invoices.paymentMethodIsCard(
						paymentMethodType
					)
						? PaymentMethod.Card
						: // PADs percentages come from the same place as ACH percentages on the organization
						paymentMethodType === PaymentMethodType.us_bank_account ||
						  paymentMethodType === PaymentMethodType.acss_debit ||
						  paymentMethodType === PaymentMethodType.link
							? PaymentMethod.Ach
							: null,
					selectedPaymentInstallmentIds: selectedInstallments.map(
						( installment ) => installment.id
					),
					gratuity: {
						value:
							gratuitySelection === SELECTION_OPTIONS.CUSTOM
								? gratuityAmount
								: parseInt( gratuitySelection ),
						isPercentage:
							gratuitySelection !== SELECTION_OPTIONS.CUSTOM &&
							gratuitySelection !== SELECTION_OPTIONS.NONE,
					},
				} );
				setTotal( values.payment );
				setGratuityAmount( values.gratuity );
			}
		};
		calculateInvoice();
	}, [
		gratuitySelection,
		gratuityAmount,
		setGratuityAmount,
		setTotal,
		paymentMethodType,
		invoice,
		selectedInstallments,
	] );

	// if the user selects a custom gratuity, we should clear any pre-existing
	useEffect( () => {
		if ( gratuitySelection === SELECTION_OPTIONS.CUSTOM ) {
			setGratuityAmount( 0 );
		}
	}, [ gratuitySelection ] );

	const selectedAmount = useMemo(
		() =>
			selectedInstallments?.reduce(
				( acc, installment ) =>
					typeof installment.amountDue === 'number'
						? acc + installment.amountDue
						: acc,
				0
			),
		[ selectedInstallments ]
	);

	// are all remaining unpaid installments selected?
	const selectedInstallmentsIncludeAllUnpaidInstallments = paymentInstallments
		?.filter( ( installment ) => installment.status === InstallmentStatus.Unpaid )
		.every( ( installment ) =>
			selectedInstallments?.some(
				( selectedInstallment ) => selectedInstallment.id === installment.id
			)
		);
	const someInstallmentsHaveGratuityPaid = paymentInstallments?.some(
		( installment ) => installment.gratuityPaid && installment.gratuityPaid > 0
	);
	// should we show the gratuity options?
	const shouldRenderGratuityOptions =
		invoice?.allowsGratuity &&
		!invoice.gratuityAmount &&
		selectedInstallmentsIncludeAllUnpaidInstallments &&
		!someInstallmentsHaveGratuityPaid;

	const contact = invoice?.contact;

	const returnUrl = useMemo( () => {
		return `${ window.location.origin }/invoice/${ invoiceId }${
			token ? `?token=${ token }` : ''
		}`;
	}, [ invoiceId, token ] );

	const setLocalStorage = useCallback(
		( selectedInstallments: { id: string }[] ) => {
			if ( invoiceId ) {
				const checkoutData: StoredInvoicePaymentData = {
					sessionPaymentInstallmentId: selectedInstallments[ 0 ].id,
					paymentInstallmentIds: selectedInstallments.map(
						( installment ) => installment.id
					),
					gratuityAmount,
					invoiceId,
					receiptEmails: transformedReceiptEmails,
					activateAutoPay: autoPaySelection,
				};
				localStorage.setItem(
					Globals.INVOICE_PAYMENT_STORAGE_KEY,
					JSON.stringify( checkoutData )
				);
			}
		},
		[
			invoiceId,
			gratuityAmount,
			transformedReceiptEmails,
			autoPaySelection
		]
	);

	const [
		createIntentForInvoicePayment,
		{
			loading: createIntentForInvoicePaymentLoading,
			error: createIntentForInvoicePaymentError,
		},
	] = usePaymentSection_CreateIntentForInvoicePaymentMutation();

	const onUpdatePaymentMethodType = useCallback(
		async ( incomingPaymentMethodType: PaymentMethodType | undefined ) => {
			if ( incomingPaymentMethodType !== paymentMethodType ) {
				setPaymentMethodType( incomingPaymentMethodType );
			}
			if ( invoiceId && incomingPaymentMethodType ) {
				setLocalStorage( selectedInstallments );
			}
		},
		[
			invoiceId,
			selectedInstallments,
			setLocalStorage,
			paymentMethodType
		]
	);

	const createPaymentIntent = useCallback( async () => {
		if ( !paymentMethodType || !sessionPaymentInstallmentId ) {
			return undefined;
		}
		paymentAttemptRef.current = getOrCreatePaymentAttempt(
			paymentAttemptRef.current,
			paymentAttemptFingerprint
		);
		const response = await createIntentForInvoicePayment( {
			variables: {
				data: {
					gratuityAmount,
					paymentAttemptId: paymentAttemptRef.current.id,
					paymentMethodType,
					sessionPaymentInstallmentId,
					token,
				},
			},
		} );
		return response.data?.createIntentForInvoicePayment.clientSecret;
	}, [
		createIntentForInvoicePayment,
		gratuityAmount,
		paymentMethodType,
		paymentAttemptFingerprint,
		sessionPaymentInstallmentId,
		token,
	] );
	const abandonPaymentAttempt = useCallback( () => {
		paymentAttemptRef.current = null;
	}, [] );

	useEffect( () => {
		setLoading(
			createIntentForInvoicePaymentLoading ||
				updateInvoiceFromPaymentSessionLoading
		);
	}, [ createIntentForInvoicePaymentLoading, updateInvoiceFromPaymentSessionLoading, ] );

	// post payment actions
	const handleOnClose = useCallback( () => {
		setSessionSecrets( undefined );
		setPaymentMethodType( undefined );
		setReceiptEmails( undefined );
		localStorage.removeItem( Globals.INVOICE_PAYMENT_STORAGE_KEY );
		onClose();
	}, [
		setPaymentMethodType,
		setReceiptEmails,
		onClose
	] );

	const handleSuccess = useCallback(
		async ( paymentIntentId: string ) => {
			const storedDataStr = localStorage.getItem(
				Globals.INVOICE_PAYMENT_STORAGE_KEY
			);
			if ( !storedDataStr ) {
				showError( 'Payment data not found. Please try again.' );
				/* refetch invoice view data to possible update statuses and other data
			that may have been out-of-date, which may have caused this error state */
				await client.refetchQueries( {
					include: [ 'invoiceDetail_getInvoiceWhere' ],
				} );
				return;
			}
			// Parse stored data
			const storedData: StoredInvoicePaymentData = JSON.parse( storedDataStr );
			const response = await updateInvoiceFromPaymentSession( {
				variables: {
					data: {
						invoiceId: storedData.invoiceId,
						receiptEmails: storedData.receiptEmails,
						activateAutoPay: storedData.activateAutoPay,
						paymentInstallmentIds: storedData.paymentInstallmentIds,
						sessionPaymentInstallmentId: storedData.sessionPaymentInstallmentId,
						gratuityAmount: storedData.gratuityAmount,
						token,
						paymentIntentId,
					},
				},
			} );
			if ( response.errors ) {
				response.errors.forEach( ( error ) => showError( error.message ) );
			} else {
				handleOnClose();
				onSuccess();
			}
		},
		[
			updateInvoiceFromPaymentSession,
			showError,
			onSuccess,
			handleOnClose,
			token,
		]
	);

	// Handle session_id returns from Stripe checkout
	const redirectStatus = new URLSearchParams( window.location.search ).get(
		'redirect_status'
	);
	const paymentIntentId = new URLSearchParams( window.location.search ).get(
		'payment_intent_id'
	);
	useEffect( () => {
		const handleCheckoutReturn = async (
			redirectStatus: string,
			paymentIntentId: string
		) => {
			if ( redirectStatus !== 'succeeded' ) {
				showError( 'Payment failed. Please try again.' );
				setLoading( false );
				return;
			}
			// Remove session_id from URL
			await router.replace(
				`/invoice/${ invoiceId }${ token ? `?token=${ token }` : '' }`
			);
			setLoading( true );
			// Get stored data from localStorage
			const storedDataStr = localStorage.getItem(
				Globals.INVOICE_PAYMENT_STORAGE_KEY
			);
			if ( !storedDataStr ) {
				showError( 'Payment data not found. Please try again.' );
				setLoading( false );
				return;
			}
			// Parse stored data
			handleSuccess( paymentIntentId );
		};
		if ( redirectStatus && paymentIntentId ) {
			handleCheckoutReturn( redirectStatus, paymentIntentId );
		}
	}, [
		handleSuccess,
		router,
		showError,
		setLoading,
		redirectStatus,
		paymentIntentId,
		invoiceId,
		token,
	] );

	// mutation error handling
	useEffect( () => {
		const paymentError = createIntentForInvoicePaymentError;
		if ( paymentError ) {
			const unfilteredErrors = rpcShared.errorFiltering.filterApiErrors(
				{
					errors: paymentError.graphQLErrors,
				},
				[
					'vendorHasStripeNeeds',
					'default',
					'cannotReceivePayments',
					'cannotPayDisputedInvoice',
				],
				{
					vendorHasStripeNeeds: () =>
						showError( rpcShared.strings.errorMessages.vendorHasStripeNeeds ),
					default: () => showError( rpcShared.strings.errorMessages.default ),
					cannotReceivePayments: () =>
						showError( rpcShared.strings.errorMessages.cannotReceivePayments ),
					cannotPayDisputedInvoice: () =>
						showError( rpcShared.strings.errorMessages.cannotPayDisputedInvoice ),
				}
			);
			if ( unfilteredErrors.errors.length > 0 ) {
				setPaymentValidationErrorModalOpen( true );
			}
		}
	}, [ showError, createIntentForInvoicePaymentError ] );

	useEffect( () => {
		if ( updateInvoiceFromPaymentSessionError ) {
			showError( updateInvoiceFromPaymentSessionError );
		}
	}, [ updateInvoiceFromPaymentSessionError, showError ] );

	return {
		loading,
		hasSubmitted,
		setLoading,
		invoiceTotal: invoice
			? rpcShared.invoices.calculateInvoiceTotalToString( invoice )
			: 0,
		selectedAmount,
		total,
		returnUrl,
		shouldRenderGratuityOptions,
		contact,
		gratuityAmount,
		setGratuityAmount,
		gratuitySelection,
		setGratuitySelection,
		validation,
		cardConvenienceFeePercentage,
		achConvenienceFeePercentage,
		organizationName: invoice?.organization?.name || '',
		organizationId: invoice?.organization?.id,
		paymentMethodType,
		onUpdatePaymentMethodType,
		sessionSecrets,
		createPaymentIntent,
		abandonPaymentAttempt,
		paymentElementAmount,
		receiptEmails,
		setReceiptEmails,
		shouldRenderAutoPayOptions,
		autoPaySelection,
		setAutoPaySelection,
		validate,
		onSuccess: handleSuccess,
		setLocalStorage: () =>
			selectedInstallments ? setLocalStorage( selectedInstallments ) : undefined,
		multiplePaymentsAtOnce: selectedInstallments
			? selectedInstallments.length > 1
			: false,
		installmentsForValidation,
		guestToken: token,
		currencyLabel: rpcShared.invoices.getCurrencyLabel(
			invoice?.organization?.country
		),
		paymentValidationErrorModalOpen,
		onClosePaymentValidationErrorModal: () => window.location.reload(),
		sessionPaymentInstallmentId,
	};
};
